import type { SurfacePoint } from '@repo/api-contracts';
import { type PointerEvent, type RefObject, useCallback, useEffect, useState } from 'react';
import { clientToPhotoPoint, type PhotoDimensions } from '@/entities/edit-area';

interface Options<P> {
  svgRef: RefObject<SVGSVGElement | null>;
  photoSize: PhotoDimensions;
  /** 점 찍기 모드. 꺼지면 열린 경로를 버린다. */
  active: boolean;
  /** 사진 px → 모을 점. null 이면 점을 찍지 않는다(예: 원통 실루엣 밖). */
  toPoint: (photoPx: SurfacePoint) => P | null;
  /** 두 점 사이의 사진 px 거리. 첫 점을 눌렀는지 판정한다. */
  distancePx: (a: P, b: P) => number;
  /** 닫으려는 점열을 받을 수 없는 이유. 받을 수 있으면 null. */
  validate: (points: readonly P[]) => string | null;
  maxPoints: number;
  maxPointsMessage: string;
  /** toPoint 가 null 일 때 보일 안내. 없으면 조용히 무시한다. */
  outsideMessage?: string;
  /** 닫은 점열. 거부 사유를 돌려주면 점을 남기고 사유를 보인다. */
  onClose: (points: P[]) => string | null;
  /** Esc 로 그만둘 때. */
  onCancel: () => void;
}

/** 첫 점을 눌렀다고 볼 반경 — 사진 긴 변의 1.2%. 화면 확대율과 무관하게 원본 px 기준이다. */
const CLOSE_RADIUS_RATIO = 0.012;
const TYPING_TAGS: readonly string[] = ['INPUT', 'TEXTAREA', 'SELECT'];

/**
 * 사진 위에 점을 찍어 닫힌 다각형을 만드는 공용 훅. 점의 좌표계(사진 %·표면 mm)는 호출자가 toPoint 로 정한다.
 * 열린 경로는 이 훅의 로컬 상태일 뿐 편집 상태(store)에 들어가지 않으므로 미저장 변경이 아니다.
 * Backspace 는 마지막 점을, Esc 는 전체를 지우고 onCancel 을 부른다(입력 칸에서 누른 키는 무시한다).
 */
export const usePolygonPointer = <P>({
  svgRef,
  photoSize,
  active,
  toPoint,
  distancePx,
  validate,
  maxPoints,
  maxPointsMessage,
  outsideMessage,
  onClose,
  onCancel,
}: Options<P>) => {
  const [points, setPoints] = useState<readonly P[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback((): void => {
    setPoints([]);
    setError(null);
  }, []);
  const removeLast = useCallback((): void => {
    setPoints((current) => current.slice(0, -1));
    setError(null);
  }, []);

  useEffect(() => {
    if (!active) reset();
  }, [active, reset]);

  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      if (target instanceof HTMLElement && TYPING_TAGS.includes(target.tagName)) return;
      if (event.key === 'Escape') {
        reset();
        onCancel();
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        removeLast();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, onCancel, removeLast, reset]);

  const isNearFirst = (point: P): boolean => {
    const first = points[0];
    if (first === undefined) return false;
    const radius = Math.max(photoSize.widthPx, photoSize.heightPx) * CLOSE_RADIUS_RATIO;
    return distancePx(point, first) <= radius;
  };

  const addPoint = (event: PointerEvent<SVGElement>): void => {
    if (!active || event.button !== 0 || svgRef.current === null) return;
    if (photoSize.widthPx <= 0 || photoSize.heightPx <= 0) return;
    const photoPoint = clientToPhotoPoint(svgRef.current, { x: event.clientX, y: event.clientY });
    if (photoPoint === null) return;
    event.preventDefault();
    const point = toPoint(photoPoint);
    if (point === null) {
      if (outsideMessage !== undefined) setError(outsideMessage);
      return;
    }

    if (isNearFirst(point)) {
      const reason = validate(points);
      if (reason !== null) {
        setError(reason);
        return;
      }
      const rejection = onClose([...points]);
      if (rejection !== null) {
        setError(rejection);
        return;
      }
      reset();
      return;
    }
    if (points.length >= maxPoints) {
      setError(maxPointsMessage);
      return;
    }
    setPoints([...points, point]);
    setError(null);
  };

  return { points, error, addPoint, removeLast, reset };
};
